// 📄 components/Configuracion/TransaccionZFormModal.tsx
// Modal de alta Y edición de una transacción Z en Configuración > Desarrollo
// Z del proyecto (ver ConfiguracionTransaccionesZ.tsx) — mismo patrón que
// NuevaApiModal.tsx: un solo modal, `transaccionInicial` presente = modo
// edición. "Guardar" persiste DIRECTO contra Firestore (transaccionZService.ts)
// al confirmar — sin botón "guardar configuración" intermedio, con
// guardando/error locales igual que NuevaApiModal.tsx/IncidenteFormModal.tsx.
import { useState } from "react";
import { X, Save, RefreshCw } from "lucide-react";
import { Timestamp } from "firebase/firestore";
import { useAuth } from "../../context/AuthContext.tsx";
import {
  TransaccionZ, TransaccionZData, SistemaAmbiente, SISTEMAS_AMBIENTE,
  crearTransaccionZ, actualizarTransaccionZ
} from "../../services/transaccionZService.ts";
import {
  CategoriaTransaccion, SolucionTransaccion, CATEGORIAS_TRANSACCION, SOLUCIONES_TRANSACCION
} from "../../services/catalogoTransaccionesService.ts";

interface TransaccionZFormModalProps {
  projectId: string;
  // Presente = modo edición; ausente = alta.
  transaccionInicial?: TransaccionZ | null;
  onGuardado: () => void;
  onCancelar: () => void;
}

// Timestamp de Firestore -> string "YYYY-MM-DD" para precargar el
// <input type="date">; sin valor guardado, el input arranca vacío.
const timestampAInputDate = (ts: Timestamp | null | undefined): string => {
  if (!ts) return "";
  return ts.toDate().toISOString().slice(0, 10);
};

export default function TransaccionZFormModal({ projectId, transaccionInicial, onGuardado, onCancelar }: TransaccionZFormModalProps) {
  const { user } = useAuth();
  const modoEdicion = !!transaccionInicial;

  const [codigo, setCodigo] = useState(transaccionInicial?.codigo || "");
  const [nombre, setNombre] = useState(transaccionInicial?.nombre || "");
  const [categoria, setCategoria] = useState<CategoriaTransaccion | "">(transaccionInicial?.categoria || "");
  const [solucion, setSolucion] = useState<SolucionTransaccion | "">(transaccionInicial?.solucion || "");
  const [rolPermiso, setRolPermiso] = useState(transaccionInicial?.rolPermiso || "");
  const [sistemasDisponibles, setSistemasDisponibles] = useState<SistemaAmbiente[]>(transaccionInicial?.sistemasDisponibles || []);
  const [fechaImplementacion, setFechaImplementacion] = useState(timestampAInputDate(transaccionInicial?.fechaImplementacion));
  const [descripcion, setDescripcion] = useState(transaccionInicial?.descripcion || "");
  const [responsable, setResponsable] = useState(transaccionInicial?.responsable || "");
  const [notas, setNotas] = useState(transaccionInicial?.notas || "");

  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleSistema = (s: SistemaAmbiente) => {
    setSistemasDisponibles((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const handleGuardar = async () => {
    setError(null);
    if (!codigo.trim()) { setError("El código es obligatorio."); return; }
    if (!nombre.trim()) { setError("El nombre es obligatorio."); return; }
    if (!categoria) { setError("Selecciona una categoría."); return; }
    if (!solucion) { setError("Selecciona una solución."); return; }

    const payload: TransaccionZData = {
      codigo: codigo.trim(),
      nombre: nombre.trim(),
      categoria,
      solucion,
      descripcion: descripcion.trim(),
      responsable: responsable.trim(),
      sistemasDisponibles,
      fechaImplementacion: fechaImplementacion ? Timestamp.fromDate(new Date(`${fechaImplementacion}T00:00:00`)) : null,
      rolPermiso: rolPermiso.trim(),
      notas: notas.trim()
    };

    setGuardando(true);
    try {
      if (modoEdicion) {
        await actualizarTransaccionZ(projectId, transaccionInicial!.id, payload);
      } else {
        await crearTransaccionZ(projectId, payload, user?.email || user?.uid || "");
      }
      onGuardado();
    } catch (err: any) {
      setError(err.message || "Error al guardar la transacción.");
    } finally {
      setGuardando(false);
    }
  };

  const intentarCerrar = () => { if (!guardando) onCancelar(); };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{modoEdicion ? "Editar transacción Z" : "Nueva transacción Z"}</h3>
          <button onClick={intentarCerrar} disabled={guardando} className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          {/* Fila 1: Código | Solución */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Código*</label>
              <input type="text" className={`${inputClass} font-mono`} value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="ej. ZARBA_MASTER" />
            </div>
            <div>
              <label className={labelClass}>Solución*</label>
              <select className={inputClass} value={solucion} onChange={(e) => setSolucion(e.target.value as SolucionTransaccion)}>
                <option value="">Selecciona una solución</option>
                {SOLUCIONES_TRANSACCION.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Fila 2: Nombre */}
          <div>
            <label className={labelClass}>Nombre*</label>
            <input type="text" className={inputClass} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="ej. Reporte maestro de proveedores" />
          </div>

          {/* Fila 3: Categoría | Rol / Permiso SAP */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Categoría*</label>
              <select className={inputClass} value={categoria} onChange={(e) => setCategoria(e.target.value as CategoriaTransaccion)}>
                <option value="">Selecciona una categoría</option>
                {CATEGORIAS_TRANSACCION.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Rol / Permiso SAP</label>
              <input type="text" className={`${inputClass} font-mono`} value={rolPermiso} onChange={(e) => setRolPermiso(e.target.value)} placeholder="ej. ZARBA_ROLE_COMPRAS" />
              <p className="text-[11px] text-gray-500 mt-1">Rol o perfil SAP que otorga acceso a esta transacción.</p>
            </div>
          </div>

          {/* Fila 4: Sistemas disponibles | Fecha de implementación */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Sistemas disponibles</label>
              <div className="flex items-center gap-4 pt-1">
                {SISTEMAS_AMBIENTE.map((s) => (
                  <label key={s} className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer">
                    <input type="checkbox" className="accent-blue-500 w-3.5 h-3.5" checked={sistemasDisponibles.includes(s)} onChange={() => toggleSistema(s)} />
                    {s}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={labelClass}>Fecha de implementación</label>
              <input type="date" className={inputClass} value={fechaImplementacion} onChange={(e) => setFechaImplementacion(e.target.value)} />
            </div>
          </div>

          {/* Fila 5: Descripción */}
          <div>
            <label className={labelClass}>Descripción</label>
            <textarea className={`${inputClass} resize-y`} rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Para qué se usa esta transacción..." />
          </div>

          {/* Fila 6: Responsable */}
          <div>
            <label className={labelClass}>Responsable</label>
            <input type="text" className={inputClass} value={responsable} onChange={(e) => setResponsable(e.target.value)} placeholder="Nombre del desarrollador/consultor responsable" />
          </div>

          {/* Fila 7: Notas técnicas */}
          <div>
            <label className={labelClass}>Notas técnicas</label>
            <textarea className={`${inputClass} resize-y`} rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Observaciones técnicas adicionales..." />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10 flex-shrink-0">
          <button type="button" onClick={intentarCerrar} disabled={guardando} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
          >
            {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {guardando ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
